<template>
  <div>
    <div v-if="loading" class="flex items-center justify-center h-full">
      <loading-spinner diameter="64px" />
    </div>

    <alert-badge
      v-else-if="error.title"
      isDanger
      :title="error.title"
      :body="error.body"
      @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <div class="m-6">
      <el-row :gutter="50">
        <el-col :lg="12">
          <template v-if="$can('read', '/reports/sales')">
            <div class="section-container">
              <div class="section-title py-2 mt-6 rounded">
                <h2 class="text-center p-0">
                  <font-awesome-icon icon="dollar-sign"></font-awesome-icon>
                  {{ $t('views.sales.sales') }}
                </h2>
              </div>
              <div class="buttons-container mt-3">
                <el-row :gutter="15">
                  <el-col v-for="(link, i) in tabs.sales" :key="i" :md="8" :xs="24">
                    <reports-link :path="link.path" :linkTitle="link.title" />
                  </el-col>
                </el-row>
              </div>
            </div>
          </template>

          <template v-if="$can('read', '/reports/inventory')">
            <div class="section-title py-2 mt-6 rounded">
              <h2 class="text-center p-0">
                <font-awesome-icon icon="warehouse"></font-awesome-icon>
                {{ $t('views.inventory.inventory') }}
              </h2>
            </div>
            <div class="buttons-container mt-3">
              <el-row :gutter="15">
                <el-col v-for="(link, i) in tabs.inventory" :key="i" :md="8" :xs="24">
                  <reports-link :path="link.path" :linkTitle="link.title" />
                </el-col>
              </el-row>
            </div>
          </template>

          <template v-if="$can('read', '/reports/customer')">
            <div class="section-title py-2 mt-6 rounded">
              <h2 class="text-center p-0">
                <font-awesome-icon icon="users"></font-awesome-icon>
                {{ $t('views.customers.customers') }}
              </h2>
            </div>
            <div class="buttons-container mt-3">
              <el-row :gutter="15">
                <el-col v-for="(link, i) in tabs.customers" :key="i" :md="8" :xs="24">
                  <reports-link :path="link.path" :linkTitle="link.title" />
                </el-col>
              </el-row>
            </div>
          </template>
        </el-col>

        <el-col :lg="12">
          <template v-if="$can('read', '/reports/accounting')">
            <div class="section-title py-2 mt-6 rounded">
              <h2 class="text-center p-0">
                <font-awesome-icon icon="calculator"></font-awesome-icon>
                {{ $t('views.accounting.accounting') }}
              </h2>
            </div>
            <div class="buttons-container mt-3">
              <el-row :gutter="15">
                <el-col v-for="(link, i) in tabs.accounting" :key="i" :md="8" :xs="24">
                  <reports-link :path="link.path" :linkTitle="link.title" :subtitle="link.subtitle" />
                </el-col>
              </el-row>
            </div>
          </template>

          <template v-if="$can('read', '/reports/hr')">
            <div class="section-title py-2 mt-6 rounded">
              <h2 class="text-center p-0">
                {{ $t('views.hr.hr') }}
              </h2>
            </div>
            <div class="buttons-container mt-3">
              <el-row :gutter="15">
                <el-col v-for="(link, i) in tabs.employees" :key="i" :md="8" :xs="24">
                  <reports-link :path="link.path" :linkTitle="link.title" />
                </el-col>
              </el-row>
            </div>
          </template>

          <template v-if="$can('read', '/reports/vendor')">
            <div class="section-title py-2 mt-6 rounded">
              <h2 class="text-center p-0">
                <font-awesome-icon icon="users"></font-awesome-icon>
                {{ $t('views.vendors.vendors') }}
              </h2>
            </div>
            <div class="buttons-container mt-3">
              <el-row :gutter="15">
                <el-col v-for="(link, i) in tabs.vendors" :key="i" :md="8" :xs="24">
                  <reports-link :path="link.path" :linkTitle="link.title" />
                </el-col>
              </el-row>
            </div>
          </template>
          <template v-if="$can('read', '/reports/examinations') && useSettingsStore().getValue('examinations_module') === 'true'">
            <div class="section-title py-2 mt-6 rounded">
              <h2 class="text-center p-0 flex items-center gap-2 justify-center">
                <i class="icon-examination text-xl" />
                {{ $t('views.examinations.examinations') }}
              </h2>
            </div>
            <div class="buttons-container mt-3">
              <el-row :gutter="15">
                <el-col v-for="(link, i) in tabs.examinations" :key="i" :md="8" :xs="24">
                  <reports-link :path="link.path" :linkTitle="link.title" />
                </el-col>
              </el-row>
            </div>
          </template>
          <template v-if="$can('read', '/reports/customer-pets') && useSettingsStore().getValue('vet_module') === 'true'">
            <div class="section-title py-2 mt-6 rounded">
              <h2 class="text-center p-0 flex items-center gap-2 justify-center">
                <font-awesome-icon icon="paw"></font-awesome-icon>
                {{ $t('views.pets.pets') }}
              </h2>
            </div>
            <div class="buttons-container mt-3">
              <el-row :gutter="15">
                <el-col v-for="(link, i) in tabs.pets" :key="i" :md="8" :xs="24">
                  <reports-link :path="link.path" :linkTitle="link.title" />
                </el-col>
              </el-row>
            </div>
          </template>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import ReportsLink from '@/components/ReportsLink.vue'
import { Route } from '@/router/routes'
import { useI18n } from 'vue-i18n'
import { useSettingsStore } from '@/stores/settingsStore'

export default defineComponent({
  components: {
    LoadingSpinner,
    AlertBadge,
    ReportsLink
  },

  setup() {
    return {
      useSettingsStore
    }
  },

  data() {
    const messages = {
      en: {
        sections: {
          sales: {
            topSoldItems: 'Top Sold Items',
            totalSales: 'Total Sales',
            totalReturns: 'Total Returns',
            weeklySales: 'Weekly Sales',
            monthlySales: 'Monthly Sales',
            salesByCashier: 'Sales By Cashier',
            salesBySalesRep: 'Sales By Sales Rep',

            leastSoldItems: 'Least Sold Items',
            salesByItem: 'Sales By Item',
            salesByCategory: 'Sales By Category',
            salesSummary: 'Sales Summary',
            salesDetails: 'Sales Details',
            registerClosing: 'Register Closing',
            endOfDay: 'End of Day',
            salesComparison: 'Sales Comparison',
            salesHeatmap: 'Sales Heatmap',
            ordersStatement: 'Orders Statement',
            salesByVendor: 'Sales by Vendor'
          },
          inventory: {
            quantity: 'Quantity Report',
            inventoryValue: 'Inventory Value',
            quickFacts: 'Quick Facts',
            lowStockInventory: 'Low Stock Inventory',
            negativeStockInventory: 'Negative Stock Inventory',
            itemsInfo: 'Items Info',
            batchTracker: 'Batch Tracker',
            expirableItemsInsight: 'Expirable Items Insight',
            allItems: 'All Items Report'
          },
          customers: {
            topCustomers: 'Top Customers',
            frequentCustomers: 'Frequent Customers',
            outstandingBalances: 'Outstanding Balances'
          },
          accounting: {
            profitAndLoss: 'Profit and Loss',
            vendorsBalances: 'Vendors Balances',
            customersBalances: 'Customers Balances',
            registerClosing: 'Register Closing',
            registerClosingSummary: 'Register Closing Summary',
            cashAssets: 'Cash Assets',
            expenses: 'Expenses',
            formOfPayments: 'Forms of Payments',
            profitByItem: 'Profit by Item'
          },
          employees: {},
          vendors: {
            topVendors: 'Top Vendors',
            outstandingBalances: 'Outstanding Balances',
            frequentVendors: 'Frequent Vendors'
          },
          hr: {
            topSalaries: 'Salaries',
            employeeAttendance: 'Employee Attendance',
            employeesCommissions: 'Employees Commissions',
            moneyCollectorCommissions: 'Money Collector Commissions'
          },
          examinations: {
            numberOfExaminations: 'General Info'
          },
          pets: {
            vetServicesByPet: 'Vet Services by Pet',
            vetAllServices: 'All Vet Services'
          }
        }
      },
      ar: {
        sections: {
          sales: {
            topSoldItems: 'المنتجات الاكثر مبيعاً',
            totalSales: 'اجمالي المبيعات',
            totalReturns: 'اجمالي المسترجعات',
            weeklySales: 'المبيعات الاسبوعية',
            monthlySales: 'المبيعات الشهرية',
            salesByCashier: 'المبيعات حسب الكاشير',
            salesBySalesRep: 'المبيعات حسب ممثل البيع',

            leastSoldItems: 'أقل المنتجات مبيعا',
            salesByItem: 'مبيعات منتج محدد',
            salesByCategory: 'مبيعات صنف محدد',
            salesSummary: 'ملخص المبيعات',
            salesDetails: 'تفاصيل المبيعات',
            endOfDay: 'نهاية اليوم',
            salesComparison: 'مقارنة المبيعات',
            salesHeatmap: 'خارطة المبيعات',
            registerClosing: 'اغلاق الصندوق',
            ordersStatement: 'كشف طلبات البيع',
            salesByVendor: 'المبيعات حسب المورد'
          },
          inventory: {
            quantity: 'كميات المواد',
            inventoryValue: 'قيمة البضاعة',
            quickFacts: 'معلومات عامة',
            lowStockInventory: 'المخزون القليل',
            negativeStockInventory: 'المخزون السلبي',
            itemsInfo: 'معلومات المواد',
            batchTracker: 'تتبع الوجبات',
            expirableItemsInsight: 'تقرير المواد المنتهية الصلاحية',
            allItems: 'جميع المواد'
          },
          customers: {
            topCustomers: 'أفضل الزبائن',
            outstandingBalances: 'الأرصدة المستحقة',
            frequentCustomers: 'الزبائن المميزون'
          },
          accounting: {
            profitAndLoss: 'الربح والخسارة',
            vendorsBalances: 'ميزانية الموردين',
            customersBalances: 'ميزانية الزبائن',
            registerClosing: 'صندوق النقد',
            registerClosingSummary: 'ملخص صندوق النقد',
            cashAssets: 'الأصول النقدية',
            expenses: 'المصاريف',
            formOfPayments: 'العملات المستلمة',
            profitByItem: 'الأرباح بحسب المنتج'
          },
          employees: {},
          vendors: {
            topVendors: 'أفضل الموردين',
            outstandingBalances: 'الأرصدة المستحقة',
            frequentVendors: 'الموردين المميزون'
          },
          hr: {
            topSalaries: 'رواتب الموظفين',
            employeeAttendance: 'حضور الموظفين',
            employeesCommissions: 'عمولات الموظفين',
            moneyCollectorCommissions: 'عمولات الجباة'
          },
          examinations: {
            numberOfExaminations: 'معلومات الفحوصات'
          },
          pets: {
            vetServicesByPet: 'الخدمات بحسب الاليف',
            vetAllServices: 'الخدمات البيطرية'
          }
        }
      }
    }

    const { t } = useI18n({
      messages
    })

    const settingsStore = useSettingsStore()
    const isOrderFulfillmentEnabled = computed(() => {
      return settingsStore.getValue('order_fulfilling_module') === 'true'
    })

    const isExpirationModuleEnabled = computed(
      () => settingsStore.getValue('expiration_module') === 'true'
    )

    const tabs = {
      sales: [
        // {
        //   title: t('sections.sales.salesByItem'),
        //   path: Route.REPORTS_SALES_SALES_BY_ITEM
        // },
        // {
        //   title: 'sales by category',
        //   path: Route.REPORTS_SALES_SALES_BY_CATEGORY
        // },
        {
          title: t('sections.sales.salesSummary'),
          path: Route.REPORTS_SALES_SALES_SUMMARY
        },
        {
          title: t('sections.sales.salesDetails'),
          path: Route.REPORTS_SALES_SALES_DETAILS
        },
        {
          title: t('sections.sales.totalReturns'),
          path: Route.REPORTS_SALES_TOTAL_RETURNS
        },
        {
          title: t('sections.sales.weeklySales'),
          path: Route.REPORTS_SALES_WEEKLY_SALES
        },
        {
          title: t('sections.sales.monthlySales'),
          path: Route.REPORTS_SALES_MONTHLY_SALES
        },
        {
          title: t('sections.sales.topSoldItems'),
          path: Route.REPORTS_SALES_TOP_SOLD_ITEMS
        },
        {
          title: t('sections.sales.salesByCashier'),
          path: Route.REPORTS_SALES_TOP_REPS
        },
        {
          title: t('sections.sales.salesBySalesRep'),
          path: Route.REPORTS_SALES_SALES_REPS
        },
        {
          title: t('sections.sales.ordersStatement'),
          path: Route.REPORTS_SALES_ORDERS_STATEMENT
        },
        {
          title: t('sections.sales.salesByVendor'),
          path: Route.REPORTS_SALES_SALES_BY_VENDOR
        }

        // {
        //   title: t('sections.sales.leastSoldItems'),
        //   path: Route.REPORTS_SALES_LEAST_SOLD_ITEMS
        // },
        // {
        //   title: t('sections.sales.salesByCategory'),
        //   path: Route.REPORTS_SALES_SALES_BY_CATEGORY
        // },
        // {
        //   title: t('sections.sales.salesSummary'),
        //   path: Route.REPORTS_SALES_SALES_SUMMARY
        // },
        // {
        //   title: t('sections.sales.registerClosing'),
        //   path: Route.REPORTS_SALES_SALES_REGISTER_CLOSING_REPORT
        // },
        // {
        //   title: t('sections.sales.endOfDay'),
        //   path: Route.REPORTS_SALES_END_OF_DAY
        // },
        // {
        //   title: t('sections.sales.salesComparison'),
        //   path: Route.REPORTS_SALES_SALES_COMPARISON
        // },
        // {
        //   title: t('sections.sales.salesHeatmap'),
        //   path: Route.REPORTS_SALES_SALES_HEATMAP
        // }
      ],
      inventory: computed(() => {
        const baseInventoryReports = [
          // {
          //   title: t('sections.inventory.quantity'),
          //   path: Route.REPORTS_INVENTORY_QUANTITY
          // },
          // {
          //   title: t('sections.inventory.inventoryValue'),
          //   path: Route.REPORTS_INVENTORY_INVENTORY_VALUE
          // }
          {
            title: t('sections.inventory.allItems'),
            path: Route.REPORTS_INVENTORY_ALL_ITEMS
          },
          {
            title: t('sections.inventory.quickFacts'),
            path: Route.REPORTS_INVENTORY_QUICK_FACTS
          },
          {
            title: t('sections.inventory.lowStockInventory'),
            path: Route.REPORTS_INVENTORY_LOW_STOCK
          },
          {
            title: t('sections.inventory.negativeStockInventory'),
            path: Route.REPORTS_INVENTORY_NEGATIVE_STOCK
          },
          {
            title: t('sections.inventory.itemsInfo'),
            path: Route.REPORTS_INVENTORY_ITEM
          }
        ]

        // Only add batch tracker and expirable items insight if expiration module is enabled
        if (isExpirationModuleEnabled.value) {
          baseInventoryReports.push({
            title: t('sections.inventory.batchTracker'),
            path: Route.REPORTS_INVENTORY_BATCH_TRACKER
          })
          baseInventoryReports.push({
            title: t('sections.inventory.expirableItemsInsight'),
            path: Route.REPORTS_INVENTORY_EXPIRABLE_ITEMS_INSIGHT
          })
        }

        return baseInventoryReports
      }),
      customers: [
        {
          title: t('sections.customers.topCustomers'),
          path: Route.REPORTS_CUSTOMERS_TOP_CUSTOMERS
        },
        {
          title: t('sections.customers.frequentCustomers'),
          path: Route.REPORTS_CUSTOMERS_FREQUENT_CUSTOMERS
        },
        {
          title: t('sections.customers.outstandingBalances'),
          path: Route.REPORTS_CUSTOMERS_OUTSTANDING_BALANCES
        }
      ],
      accounting: [
        // {
        //   title: t('sections.accounting.profitAndLoss'),
        //   path: Route.REPORTS_ACCOUNTING_PROFIT_AND_LOSS
        // },
        // {
        //   title: t('sections.accounting.vendorsBalances'),
        //   path: Route.REPORTS_VENDORS_VENDORS_BALANCES
        // },
        // {
        //   title: t('sections.accounting.customersBalances'),
        //   path: Route.REPORTS_ACCOUNTING_CUSTOMERS_BALANCES
        // },
        {
          title: t('sections.accounting.registerClosing'),
          path: Route.REPORTS_ACCOUNTING_REGISTER_CLOSING
        },
        {
          title: t('sections.accounting.registerClosingSummary'),
          path: Route.REPORTS_ACCOUNTING_REGISTER_CLOSING_SUMMARY
        },
        {
          title: t('sections.accounting.cashAssets'),
          path: Route.REPORTS_ACCOUNTING_CASH_ASSETS
        },
        {
          title: t('sections.accounting.expenses'),
          path: Route.REPORTS_ACCOUNTING_EXPENSES
        },
        {
          title: t('sections.accounting.formOfPayments'),
          path: Route.REPORTS_ACCOUNTING_FORMS_OF_PAYMENTS
        },
        {
          title: t('sections.accounting.profitByItem'),
          path: Route.REPORTS_ACCOUNTING_PROFIT_BY_ITEM
        },
        {
          title: t('sections.accounting.profitAndLoss'),
          path: Route.REPORTS_ACCOUNTING_PROFIT_AND_LOSS
        }
      ],
      employees: [
        // {
        //   title: 'users',
        //   path: '#'
        // },
        // {
        //   title: 'attendance',
        //   path: '#'
        // },
        // {
        //   title: 'time off requests',
        //   path: '#'
        // },
        // {
        //   title: 'employees ranking',
        //   path: '#'
        // }
        {
          title: t('sections.hr.topSalaries'),
          path: Route.REPORTS_HR_TOP_SALARIES
        },
        {
          title: t('sections.hr.employeeAttendance'),
          path: Route.REPORTS_HR_EMPLOYEE_ATTENDANCE
        },
        {
          title: t('sections.hr.employeesCommissions'),
          path: Route.REPORTS_HR_EMPLOYEE_COMMISSION
        },
        {
          title: t('sections.hr.moneyCollectorCommissions'),
          path: Route.REPORTS_HR_MONEY_COLLECTOR_COMMISSION
        }
      ],
      vendors: [
        {
          title: t('sections.vendors.topVendors'),
          path: Route.REPORTS_VENDORS_TOP_VENDORS
        },
        {
          title: t('sections.vendors.outstandingBalances'),
          path: Route.REPORTS_VENDORS_OUTSTANDING_BALANCES
        },
        {
          title: t('sections.vendors.frequentVendors'),
          path: Route.REPORTS_VENDORS_FREQUENT_VENDORS
        }
      ],
      examinations: computed(() => {
        if (settingsStore.getValue('examinations_module') === 'true') {
          const baseExaminationsReports = [
            {
              title: t('sections.examinations.numberOfExaminations'),
              path: Route.REPORTS_EXAMINATIONS_NUMBER_OF_EXAMINATIONS
            }
          ]
          return baseExaminationsReports
        }
        return []
      }),
      pets: computed(() => {
        if (settingsStore.getValue('vet_module') === 'true') {
          const basePetsReports = [
            {
              title: t('sections.pets.vetServicesByPet'),
              path: Route.REPORTS_PETS_VET_SERVICES_BY_PET
            },
            {
              title: t('sections.pets.vetAllServices'),
              path: Route.REPORTS_PETS_VET_ALL_SERVICES
            }
          ]
          return basePetsReports
        }
        return []
      })
    }

    return {
      error: {
        title: '',
        body: ''
      },
      loading: false,
      tabs
    }
  },
  methods: {}
})
</script>

<style scoped>
.section-title {
  background: var(--tertiary-bg-color);
  color: var(--tertiary-text-color);
}
</style>
